import React from 'react';
import i18next from 'i18next';
import { createColumnHelper } from '@tanstack/react-table';
import { OneWayInServiceProviderDto } from 'src/api/v2';
import { Tag, Tooltip } from '@innovationnorway/cl-react';
import { Link } from 'react-router-dom';
import { ROUTES } from 'src/navigation';

const columnHelper = createColumnHelper<OneWayInServiceProviderDto>();

const { t } = i18next;

const TruncatedCell: React.FC<{ tooltip: string; children: React.ReactNode }> = ({ tooltip, children }) => {
  const [isTruncated, setIsTruncated] = React.useState(false);
  const observerRef = React.useRef<ResizeObserver | null>(null);

  // Callback ref so the observer re-attaches whenever the span node changes
  const setNode = React.useCallback((node: HTMLSpanElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    const measure = () => setIsTruncated(node.scrollWidth > node.clientWidth);
    measure();
    observerRef.current = new ResizeObserver(measure);
    observerRef.current.observe(node);
  }, []);

  const content = (
    <span ref={setNode} className="table-cell__truncate">
      {children}
    </span>
  );

  // Only render tooltip when the content is truncated
  return isTruncated ? <Tooltip content={tooltip}>{content}</Tooltip> : content;
};

export const referralColumns = [
  columnHelper.accessor((row) => row.name, {
    id: 'actorName',
    cell: (info) => {
      return info.row.original.id && info.getValue()?.trim() !== '' ? (
        <Link
          target="_blank"
          rel="noopener noreferrer"
          to={`${ROUTES.ACTOR_PROFILE.BASE}/${info.row.original.id}`}
        >
          {info.getValue()}
        </Link>
      ) : (
        info.getValue()
      );
    },
  }),
  columnHelper.accessor(
    (row) =>
      t<string>(`clusterStatus.${row.clusterStatusCode as 'OWI_ACTOR' | 'INCUBATOR' | 'BUSINESS_GARDEN'}`),
    {
      id: 'statusName',
    },
  ),
  columnHelper.accessor((row) => row.county, {
    id: 'county',
    cell: (info) => {
      return <span>{info.getValue()}</span>;
    },
  }),
  columnHelper.accessor((row) => row.municipalities, {
    id: 'municipalities',
    header: t<string>('oneWayIn:municipalityFirstLineFor'),
    enableSorting: false,
    maxSize: 60,
    cell: (info) => {
      const value = info.getValue()?.join(', ') || '';
      if (!value) return null;
      return <TruncatedCell tooltip={value}>{value}</TruncatedCell>;
    },
  }),
  columnHelper.accessor((row) => row.deliveryTypes, {
    id: 'deliveryTypes',
    enableSorting: false,
    maxSize: 120,
    cell: (info) => {
      const deliveryTypes = info.getValue() ?? [];
      if (deliveryTypes.length === 0) return null;
      const labels = deliveryTypes.map((type) => t<string>(`oneWayIn:deliveryType.${type}`));
      return (
        <TruncatedCell tooltip={labels.join(', ')}>
          {deliveryTypes.map((type) => (
            <Tag key={type} data-color={'accent' as const}>
              {t<string>(`oneWayIn:deliveryType.${type}`)}
            </Tag>
          ))}
        </TruncatedCell>
      );
    },
  }),
  columnHelper.accessor((row) => row.portfolioIndustries, {
    id: 'portfolioIndustries',
    enableSorting: false,
    maxSize: 120,
    cell: (info) => {
      const industries = info.getValue() ?? [];
      if (industries.length === 0) return null;
      return (
        <TruncatedCell tooltip={industries.map((industry) => industry.name).join(', ')}>
          {industries.map((industry) => (
            <Tag key={industry.id} data-color={'accent' as const}>
              {industry.name}
            </Tag>
          ))}
        </TruncatedCell>
      );
    },
  }),
];
